"use client";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { fetchSubscriptionDetails } from "@/store/modules/SubscriptionsAdmin/thunks";
import { clearSubscriptionDetails } from "@/store/modules/SubscriptionsAdmin/slice";
import { useApiReady } from "@/hooks";

interface UseSubscriptionDetailsProps {
  defaultDateRange?: {
    start: Date;
    end: Date;
  };
}

const useSubscriptionDetails = ({
  defaultDateRange,
}: UseSubscriptionDetailsProps = {}) => {
  // Data padrão: início do mês atual até hoje
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const [dateRange, setDateRange] = useState({
    start: defaultDateRange?.start || startOfMonth,
    end: defaultDateRange?.end || now,
  });

  const [filters, setFilters] = useState({
    client_id: "",
    subscription_id: "",
    status: "",
    plan_id: "",
  });

  const dispatch: AppDispatch = useDispatch();
  const { isReady: isApiReady } = useApiReady(2000);

  // Selectors
  const subscriptionDetails = useSelector(
    (state: RootState) => state.subscription.subscriptionDetails,
  );
  const loading = useSelector(
    (state: RootState) => state.subscription.loadingSubscriptionDetails,
  );
  const error = useSelector((state: RootState) => state.subscription.error);

  // Função para buscar dados dos detalhes
  const fetchDetails = async (customParams?: any) => {
    const params = {
      start_date: dateRange.start.toISOString().split("T")[0],
      end_date: dateRange.end.toISOString().split("T")[0],
      ...filters,
      ...customParams,
    };

    // Remove parâmetros vazios
    Object.keys(params).forEach((key) => {
      if (
        params[key] === "" ||
        params[key] === null ||
        params[key] === undefined
      ) {
        delete params[key];
      }
    });

    await dispatch(fetchSubscriptionDetails({ params }));
  };

  // Função para atualizar filtros
  const updateFilters = (newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Função para atualizar período
  const updateDateRange = (newRange: { start: Date; end: Date }) => {
    setDateRange(newRange);
  };

  // Função para limpar dados
  const clearData = () => {
    dispatch(clearSubscriptionDetails());
  };

  // Função para aplicar filtros
  const applyFilters = () => {
    fetchDetails();
  };

  // Função para resetar filtros
  const resetFilters = () => {
    setFilters({
      client_id: "",
      subscription_id: "",
      status: "",
      plan_id: "",
    });
    setDateRange({
      start: startOfMonth,
      end: now,
    });
  };

  // Carregar dados iniciais
  useEffect(() => {
    if (!isApiReady) return;
    fetchDetails();

    // Cleanup ao desmontar o componente
    return () => {
      clearData();
    };
  }, [isApiReady]); // Só executa quando API estiver pronta

  return {
    // Estado
    subscriptionDetails,
    loading,
    error,
    dateRange,
    filters,

    // Funções
    fetchDetails,
    updateFilters,
    updateDateRange,
    clearData,
    applyFilters,
    resetFilters,

    // Dados processados (se necessário)
    detailsData: subscriptionDetails?.data || [],
    summary: subscriptionDetails?.data.summary || null,
    statistics: subscriptionDetails?.data.statistics || null,
    totalRecords: subscriptionDetails?.data.total || 0,
  };
};

export default useSubscriptionDetails;
